import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction, IRequestExecutionContext, IResponseExecutionContext } from "@/Common/Stores/Actions/BaseAction";
import { InvalidateAndDeleteRvqImmunizationRequest } from "@/Patient/Components/MedicalItems/Immunization/RequestModels/InvalidateAndDeleteRvqImmunizationRequest";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemOrder } from "@/Patient/Models/MedicalSummary/MedicalItemOrder";
import { ObstetricalSummary } from "@/Patient/Models/MedicalSummary/ObstetricalSummary";

class PatientMedicalSummaryActions extends BaseAction {
    constructor(onBeforeExecute?: IRequestExecutionContext, onBeforeHandleResponse?: IResponseExecutionContext) {
        super(onBeforeExecute, onBeforeHandleResponse);
    }

    public LoadPatientCumulativeProfile(
        patientContactId: string,
        patientContactFacetId: string,
        loadEncounters: boolean = true,
        resolveLookups: boolean = true,
        reason: string,
        then: () => void = null,
        reloadDisplayedEncounters: boolean = false
    ): void {
        patientStore.IsLoading = true;

        if ((patientContactId && patientContactId.length >= 0) || (patientContactFacetId && patientContactFacetId.length >= 0)) {
            var requestData = {
                PatientContactId: patientContactId,
                PatientInactivableId: patientContactFacetId,
                LoadEncounters: loadEncounters,
                ResolveLookups: resolveLookups,
                Reason: reason
            };

            const subOperation = reloadDisplayedEncounters ? SubServerOperations.MedicalNote.LoadSummaryAndReloadDisplayedEncounters : SubServerOperations.MedicalNote.LoadSummaryAndKeepDisplayedEncounters;
            this.Execute(ServerOperations.Server.Myle_Patient_CPP_Load, requestData, subOperation, null, null, then);
        }
    }

    public LoadPatientPendingItems(patientContactId: string): void {
        patientStore.IsLoading = true;

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_GetPendingItems, {
            PatientContactId: patientContactId
        });
    }

    public GenerateNotesPDF(patientContactId: string, dateFrom: Date, dateTo: Date, encounterContexts?: string[]): void {
        var request = {
            PatientContactId: patientContactId,
            WithSignature: false,
            DateFrom: dateFrom,
            DateTo: dateTo,
            ShowDiagnosisCode: settingsStore.configuration.get("ShowDiagnosisCode"),
            encounterContexts: encounterContexts
        };

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_GenerateNotePDF, request);
    }

    public ReorderEMRTemplateItems(patientContactId: string, medicalItemTypes: MedicalItemTypeEnum[], itemsWithNewOrder: MedicalItem[]): void {
        var reorderInfoList = itemsWithNewOrder.map(x => {
            var orderItem = new MedicalItemOrder();
            orderItem.EMRTemplateId = x.EMRTemplateId;
            orderItem.EnumMedicalItemSubType = x.EMRTemplateMedicalItemSubType == null || x.EMRTemplateMedicalItemSubType == undefined ? null : x.EMRTemplateMedicalItemSubType;
            orderItem.SortIndex = x.EMRTemplateSortIndex == 99999 || x.EMRTemplateSortIndex == null || x.EMRTemplateSortIndex == undefined ? null : x.EMRTemplateSortIndex;
            return orderItem;
        });

        var requestData = {
            PatientContactId: patientContactId,
            ChangedMedicalItemTypes: medicalItemTypes,
            ReorderedItemsList: reorderInfoList
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_CPP_Reorder, requestData, null, true);
    }

    public LoadEMRTemplateComments(emrTemplateId: string): void {
        var requestData = {
            EMRTemplateId: emrTemplateId
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_EMRTemplateComments_Get, requestData, null, true);
    }

    public SaveEMRTemplateComment(emrTemplateId: string, employeeId: string, comment: string): void {
        var requestData = {
            EMRTemplateId: emrTemplateId,
            EmployeeId: employeeId,
            Comment: comment

        };

        return this.Execute(ServerOperations.Server.Myle_Patient_EMRTemplateComments_Save, requestData, null, true);
    }

    public ToggleValidity(emrTemplateId: string, employeeId: string, comment: string): void {
        const requestData = {
            EMRTemplateId: emrTemplateId,
            EmployeeId: employeeId,
            Note: comment
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_EMRTemplate_ToggleValidity, requestData, null, true);
    }

    public ToggleValidityForImmunization(emrTemplateId: string, employeeId: string, comment: string, medicalItemId: string): void {
        const requestData = new InvalidateAndDeleteRvqImmunizationRequest({
            EmrTemplateId: emrTemplateId,
            EmployeeId: employeeId,
            Note: comment,
            MedicalItemId: medicalItemId
        });

        return this.Execute(ServerOperations.Server.QuebecVaccinationRegistry_InvalidateAndDeleteImmunization, requestData, null, true);
    }

    public ToggleVisibility(emrTemplateId: string, isDisplayedInCPP: boolean = null): void {
        var requestData = {
            EMRTemplateId: emrTemplateId,
            IsDisplayedInCPP: isDisplayedInCPP
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_EMRTemplate_ToggleCPPDisplay, requestData, null, true);
    }

    public SetMedicalSummaryVisibilityWhenPrinting(emrTemplateId: string, isVisibleOnExport: boolean): void {
        const requestData = {
            EMRTemplateId: emrTemplateId,
            IsVisibleOnExport: isVisibleOnExport
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_SetMedicalSummaryVisibilityForExport, requestData, null, true);
    }

    public MedicalALertActivation(emrTemplateIdsToActivate: string[] = [], emrTemplateIdsToDeactivate: string[] = []): void {
        var requestData =
        {
            EMRTemplateIdsToDeactivate: emrTemplateIdsToDeactivate,
            EMRTemplateIdsToActivate: emrTemplateIdsToActivate
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_CPP_MedicalAlertActivation, requestData, null, true);
    }

    public reloadFollowUpScreeningData(): void {
        if (!cacheStore.ClinicSettings.EnableFollowUps)
            return;

        this.Execute(ServerOperations.Server.Myle_LoadScreeningsAndFollowUps, {
            PatientContactId: patientStore.getCurrentPatientContactId()
        });
    }

    public saveObstetricalSummary(obstetricalSummary: ObstetricalSummary): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_MedicalSummary_SaveObstetricalSummary,
            { ObstetricalSummary: obstetricalSummary }
        );
    }

    public loadObstetricalSummary(patientContactFacetId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_MedicalSummary_LoadObstetricalSummary,
            { PatientContactFacetId: patientContactFacetId }
        );
    }

    /**
     * Load encounter forms titles by encounter ID for a patient.
     * @param patientContactId The patient contact ID to filter the forms.
     */
    public loadEncounterFormsTitles(patientContactId: string): void {
        const encounterIds = patientStore.PatientMedicalSummaryModels[patientContactId]?.get("Encounters")?.map(p => p.Id).toArray();
        if (!encounterIds?.any())
            return;

        this.Execute(
            ServerOperations.Server.Myle_GetEncounterFormsTitles,
            { PatientContactId: patientContactId, EncounterIds: encounterIds }
        );
    }
}

export { PatientMedicalSummaryActions };
